import { defineMessages } from 'react-intl.macro';
import { Collapsible } from '@tmapy/style-guide';
import { useDispatch } from '@tmapy/redux';

import './VydejMenu.scss';

import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage } from '@tmapy/intl';

import { useAuth, useSelector } from '../hooks';
import { setMenuRouteIsCollapsed } from '../redux/menu/actions';
import { Menu } from '../app/components/Menu';
import { FilterLink } from '../FilterLink';

const msg = defineMessages({
  title: {
    id: 'vydej.title',
    defaultMessage: '{customerType, select, ipr {eVýdej} other {Výdej}}',
  },
  subtitle: {
    id: 'vydej.subtitle',
    defaultMessage: '{customerType, select, ipr {} other { }}',
  },
  orders: {
    id: 'vydej.menu.orders',
    defaultMessage: 'Žádosti o data',
  },
  publishsets: {
    id: 'vydej.menu.publishsets',
    defaultMessage: 'Výdejní sady',
  },
  tags: {
    id: 'vydej.menu.tags',
    defaultMessage: 'Štítky výdejních sad',
  },
  datasources: {
    id: 'vydej.menu.datasources',
    defaultMessage: 'Datové zdroje',
  },
  exportTools: {
    id: 'vydej.menu.exportTools',
    defaultMessage: 'Exportní nástroje',
  },
  priceList: {
    id: 'vydej.menu.priceList',
    defaultMessage: 'Ceník',
  },
  serviceOrders: {
    id: 'vydej.menu.serviceOrders',
    defaultMessage: 'Žádosti bez exportu',
  },
  list: {
    id: 'vydej.menu.list',
    defaultMessage: 'Přehled žádostí o data',
  },
  listPublish: {
    id: 'vydej.menu.listPublish',
    defaultMessage: 'Dokončené',
  },
  listProcess: {
    id: 'vydej.menu.listProcess',
    defaultMessage: 'Rozpracované',
  },
  listApprove: {
    id: 'vydej.menu.listApprove',
    defaultMessage: 'Čekající na schválení',
  },
  listFail: {
    id: 'vydej.menu.listFail',
    defaultMessage: 'Chyba',
  },
  chart: {
    id: 'manager.menu.chart',
    defaultMessage: 'Statistika (grafy)',
  },
  epsiOrders: {
    id: 'vydej.menu.epsiOrders',
    defaultMessage: 'Žádosti o výdej EPSI',
  },
  epsiList: {
    id: 'vydej.menu.epsiList',
    defaultMessage: 'Přehled žádostí o výdej EPSI',
  },
  epsiListToApprove: {
    id: 'vydej.menu.epsiListToApprove',
    defaultMessage: 'Ke schválení',
  },
  epsiListApproved: {
    id: 'vydej.menu.epsiListApproved',
    defaultMessage: 'Schválené',
  },
  epsiListRejected: {
    id: 'vydej.menu.epsiListRejected',
    defaultMessage: 'Zamítnuté',
  },
});

export const VydejMenu: React.FC = () => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const formatMessage = useMessage();
  const { homeLink, customerType } = useSelector((state) => state.app.config);
  const hideTags = useSelector((state) => state.app.config.vydej?.hideTags);
  const hasEpsiModule = useSelector((state) => state.app.config.vydej?.modules?.epsi);
  const userDocumentation = useSelector((state) => state.app.config.vydej?.userDocumentation);
  const collapsible = useSelector((state) => state.menu.collapsible);

  const onCollapseClick = useCallback(
    (e: React.MouseEvent, eventData: { data: string }) => {
      dispatch(
        setMenuRouteIsCollapsed({
          id: eventData.data,
          isCollapsible: !collapsible[eventData.data],
        }),
      );
    },
    [collapsible, dispatch],
  );

  return (
    <Menu
      title={formatMessage(msg.title, { customerType })}
      subtitle={formatMessage(msg.subtitle, { customerType })}
      appLink='vydejHome'
      homeLink={homeLink}
      userDocumentation={userDocumentation}
    >
      <ul className='sg-u-vs-4'>
        <li>
          <p className='sg-u-type-sideNavHeading-inverse'>
            <FormattedMessage {...msg.orders} />
          </p>
          <ul className='sg-a-mt-1 sg-u-vs-1'>
            {auth.hasPermission('Orders', 'create', null) && (
              <li>
                <FilterLink id='vydejNewOrder' to='vydejNewOrder' />
              </li>
            )}
            <Collapsible
              isClosed={!!collapsible.vydejOrderList}
              data={'vydejOrderList'}
              onClick={onCollapseClick}
              headElement={
                <FilterLink
                  id='vydejOrderList'
                  to='vydejOrderList'
                  label={formatMessage(msg.list)}
                  filter={{}}
                />
              }
            >
              <li>
                <ul className='sg-a-mt-1 sg-u-vs-1 dmx-menu-group'>
                  <li>
                    <FilterLink
                      id='vydejOrderListPublish'
                      to='vydejOrderList'
                      label={formatMessage(msg.listPublish)}
                      filter={{ state__in: ['EXPIRED', 'PUBLISHED', 'REJECTED'] }}
                    />
                  </li>
                  <li>
                    <FilterLink
                      id='vydejOrderListProcess'
                      to='vydejOrderList'
                      label={formatMessage(msg.listProcess)}
                      filter={{
                        state__in: ['WAITING_FOR_PROCESSING', 'PROCESSING', 'INTERRUPTED'],
                      }}
                    />
                  </li>
                  <li>
                    <FilterLink
                      id='vydejOrderListApprove'
                      to='vydejOrderList'
                      label={formatMessage(msg.listApprove)}
                      filter={{
                        state__in: ['WAITING_FOR_APPROVAL'],
                      }}
                    />
                  </li>
                  <li>
                    <FilterLink
                      id='vydejOrderListFail'
                      to='vydejOrderList'
                      label={formatMessage(msg.listFail)}
                      filter={{
                        state__in: ['FAILED'],
                      }}
                    />
                  </li>
                </ul>
              </li>
            </Collapsible>
          </ul>
        </li>
        {customerType === 'ipr' && auth.hasPermission('ServiceOrders', 'list', null) && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.serviceOrders} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              <li>
                <FilterLink id='vydejNewServiceOrder' to='vydejNewServiceOrder' />
              </li>
              <li>
                <FilterLink id='vydejServiceOrderList' to='vydejServiceOrderList' />
              </li>
            </ul>
          </li>
        )}
        {hasEpsiModule && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.epsiOrders} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              <Collapsible
                isClosed={!!collapsible.vydejEpsiOrderList}
                data={'vydejEpsiOrderList'}
                onClick={onCollapseClick}
                headElement={
                  <FilterLink
                    id='vydejEpsiOrderList'
                    to='vydejEpsiOrderList'
                    label={formatMessage(msg.epsiList)}
                    filter={{}}
                  />
                }
              >
                <li>
                  <ul className='sg-a-mt-1 sg-u-vs-1 dmx-menu-group'>
                    <li>
                      <FilterLink
                        id='vydejEpsiOrderListToApprove'
                        to='vydejEpsiOrderList'
                        label={formatMessage(msg.epsiListToApprove)}
                        filter={{ state__in: ['WAITING_FOR_APPROVAL'] }}
                      />
                    </li>
                    <li>
                      <FilterLink
                        id='vydejEpsiOrderListApproved'
                        to='vydejEpsiOrderList'
                        label={formatMessage(msg.epsiListApproved)}
                        filter={{ state__in: ['APPROVED'] }}
                      />
                    </li>
                    <li>
                      <FilterLink
                        id='vydejEpsiOrderListRejected'
                        to='vydejEpsiOrderList'
                        label={formatMessage(msg.epsiListRejected)}
                        filter={{ state__in: ['REJECTED'] }}
                      />
                    </li>
                  </ul>
                </li>
              </Collapsible>
            </ul>
          </li>
        )}
        {auth.hasPermission('Publishsets', 'list.view', null) && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.publishsets} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              {auth.hasPermission('Publishsets', 'create.view', null) && (
                <li>
                  <FilterLink id='vydejNewPublishset' to='vydejNewPublishset' />
                </li>
              )}
              <li>
                <FilterLink id='vydejPublishsetList' to='vydejPublishsetList' />
              </li>
            </ul>
          </li>
        )}
        {auth.hasPermission('Tags', 'list.view', null) && !hideTags && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.tags} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              {auth.hasPermission('Tags', 'create.view', null) && (
                <li>
                  <FilterLink id='vydejNewTag' to='vydejNewTag' />
                </li>
              )}
              <li>
                <FilterLink id='vydejTagList' to='vydejTagList' />
              </li>
            </ul>
          </li>
        )}
        {auth.hasPermission('DatasetDatasources', 'update', null) && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.datasources} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              <li>
                <FilterLink id='vydejDatasourceList' to='vydejDatasourceList' />
              </li>
            </ul>
          </li>
        )}
        {auth.hasPermission('ExportTools', 'retrieve', null) && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.exportTools} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              <li>
                <FilterLink id='vydejNewExportTool' to='vydejNewExportTool' />
              </li>
              <li>
                <FilterLink id='vydejExportToolList' to='vydejExportToolList' />
              </li>
            </ul>
          </li>
        )}
        {customerType === 'ipr' && auth.hasPermission('PriceListItems', 'retrieve', null) && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.priceList} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              <li>
                <FilterLink id='vydejNewPriceListItem' to='vydejNewPriceListItem' />
              </li>
              <li>
                <FilterLink id='vydejPriceList' to='vydejPriceList' />
              </li>
            </ul>
          </li>
        )}
        {/* CHARTS */}
        {auth.hasPermission('RelatedApps', 'action.allow_charts', null) && (
          <li>
            <p className='sg-u-type-sideNavHeading-inverse'>
              <FormattedMessage {...msg.chart} />
            </p>
            <ul className='sg-a-mt-1 sg-u-vs-1'>
              <li>
                <FilterLink id='vydejChartDoc' to='vydejChartDoc' />
              </li>
            </ul>
          </li>
        )}
      </ul>
    </Menu>
  );
};
